// pages/public/AcceptInvitationPage.tsx
import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, Mail, Lock, X } from "lucide-react";
import useToken from "../../hooks/features/auth/useToken";
import { agentService } from "../../services/agentService";
import { authService } from "../../services/authService";
import { alertError, alertSuccess } from "../../helpers/alertError";
import { encrypt } from "../../config/aes";

export default function AcceptInvitationPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { saveTokens } = useToken();
    const token = searchParams.get("token");

    const [loading, setLoading] = useState(true);
    const [invitation, setInvitation] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);

    // États pour le login/register
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [passwordConfirmation, setPasswordConfirmation] = useState("");
    const [firstname, setFirstname] = useState("");
    const [lastname, setLastname] = useState("");
    const [phonenumber, setPhonenumber] = useState("");
    const [mode, setMode] = useState<"login" | "register">("register");
    const [submitting, setSubmitting] = useState(false);

    // Vérifier l'invitation
    useEffect(() => {
        if (!token) {
            setError("Lien d'invitation invalide");
            setLoading(false);
            return;
        }

        const checkInvitation = async () => {
            try {
                const data = await agentService.checkInvitation(token);
                setInvitation(data.invitation);
                setEmail(data.invitation.email);
                setLoading(false);
            } catch (err: any) {
                setError(err.response?.data?.message || "Lien d'invitation invalide ou expiré");
                setLoading(false);
            }
        };

        checkInvitation();
    }, [token]);

    // Connexion avec Izilife Pay
    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        const secureEmail = encrypt(email);
        const securePassword = encrypt(password);
        try {
            const response = await authService.login(secureEmail, securePassword);
            const izilifeToken = response.izilife_token;

            // Accepter l'invitation avec le token Izilife
            const acceptResponse = await agentService.acceptInvitation(token!, izilifeToken);
            saveTokens(acceptResponse.token, izilifeToken);
            alertSuccess("Invitation acceptée avec succès !");
            navigate("/agent/dashboard");
        } catch (err: any) {
            alertError(err.response?.data?.message || "Erreur de connexion");
        } finally {
            setSubmitting(false);
        }
    };

    // Inscription (le frontend appelle Izilife Pay)
    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            // 1. Créer le compte sur Izilife Pay
            const registerResponse = await authService.register({
                first_name: firstname,
                last_name: lastname,
                email: email,
                phonenumber: phonenumber,
                password: password,
                password_confirmation: passwordConfirmation,
                country_id: 1
            });

            const izilifeToken = registerResponse.izilife_token;

            // 2. Accepter l'invitation avec le token Izilife
            const acceptResponse = await agentService.acceptInvitation(token!, izilifeToken);
            saveTokens(acceptResponse.token, izilifeToken);
            alertSuccess("Compte créé et invitation acceptée !");
            navigate("/agent/dashboard");
        } catch (err: any) {
            alertError(err.response?.data?.message || "Erreur lors de l'inscription");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen px-4 bg-gray-50">
                <div className="text-center max-w-sm w-full bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
                    <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <X className="w-8 h-8 text-red-500" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">Lien invalide</h2>
                    <p className="text-gray-500 mt-2 text-sm">{error}</p>
                    <button
                        onClick={() => navigate("/login")}
                        className="mt-6 w-full py-2.5 bg-primary text-white font-medium rounded-xl hover:bg-primary/90 shadow-sm transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-primary/10"
                    >
                        Retour à la connexion
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4 py-12">
            <div className="w-full max-w-md">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex justify-center mb-4">
                        <img src="/logo.png" alt="Izi CallCenter" className="h-16" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        Rejoindre {invitation?.entreprise?.name || "Izi CallCenter"}
                    </h1>
                    <p className="text-sm text-gray-500 mt-2">
                        Vous avez été invité à rejoindre l'équipe en tant qu'agent
                    </p>
                    {invitation?.departement && (
                        <p className="text-sm font-medium text-primary mt-1">
                            Département : {invitation.departement}
                        </p>
                    )}
                </div>

                {/* Choix du mode */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                    <div className="flex border-b border-gray-200 mb-6">
                        <button
                            onClick={() => setMode("register")}
                            className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors duration-300 ${
                                mode === "register"
                                    ? "border-primary text-primary"
                                    : "border-transparent text-gray-500 hover:text-gray-700"
                            }`}
                        >
                            Créer un compte
                        </button>
                        <button
                            onClick={() => setMode("login")}
                            className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors duration-300 ${
                                mode === "login"
                                    ? "border-primary text-primary"
                                    : "border-transparent text-gray-500 hover:text-gray-700"
                            }`}
                        >
                            J'ai déjà un compte
                        </button>
                    </div>

                    {/* Formulaire d'inscription */}
                    {mode === "register" && (
                        <form onSubmit={handleRegister} className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                                    <input
                                        type="text"
                                        value={firstname}
                                        onChange={(e) => setFirstname(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/10 focus:border-primary focus:outline-none transition-all duration-300"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                                    <input
                                        type="text"
                                        value={lastname}
                                        onChange={(e) => setLastname(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/10 focus:border-primary focus:outline-none transition-all duration-300"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    <input
                                        type="email"
                                        value={email}
                                        disabled
                                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl bg-gray-50 text-gray-400 select-none cursor-not-allowed"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                                <input
                                    type="tel"
                                    value={phonenumber}
                                    onChange={(e) => setPhonenumber(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/10 focus:border-primary focus:outline-none transition-all duration-300"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe *</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/10 focus:border-primary focus:outline-none transition-all duration-300"
                                        required
                                        minLength={8}
                                    />
                                </div>
                                <p className="text-xs text-gray-400 mt-1">Minimum 8 caractères</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer le mot de passe *</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    <input
                                        type="password"
                                        value={passwordConfirmation}
                                        onChange={(e) => setPasswordConfirmation(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/10 focus:border-primary focus:outline-none transition-all duration-300"
                                        required
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full mt-2 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 font-medium shadow-sm transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 focus:outline-none focus:ring-4 focus:ring-primary/10"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>Création en cours...</span>
                                    </>
                                ) : (
                                    <span>Créer mon compte et rejoindre l'équipe</span>
                                )}
                            </button>
                        </form>
                    )}

                    {/* Formulaire de connexion */}
                    {mode === "login" && (
                        <form onSubmit={handleLogin} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/10 focus:border-primary focus:outline-none transition-all duration-300"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/10 focus:border-primary focus:outline-none transition-all duration-300"
                                        required
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full mt-2 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 font-medium shadow-sm transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 focus:outline-none focus:ring-4 focus:ring-primary/10"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>Connexion en cours...</span>
                                    </>
                                ) : (
                                    <span>Se connecter et rejoindre l'équipe</span>
                                )}
                            </button>
                        </form>
                    )}
                </div>

                <p className="text-center text-xs text-gray-400 mt-6 px-4">
                    En continuant, vous acceptez les conditions d'utilisation et la politique de confidentialité.
                </p>
            </div>
        </div>
    );
}